import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createInstrument, getInstrumentByID, editInstrument } from '../../store/actions/instrument/instrument.action';
import CreateInstrument from '../../components/Instruments/CreateInstrument';
import { getAwsKey } from '../../store/actions/piece-video/piece-video.action';
import PageLoader from '../../components/Common/PageLoader';

const CreateInstrumentContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.instrument.isCreating);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [instrumentToBeEdit, setInstrumentToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const instrumentCategory = categories.find((category) => category.link === 'instruments');
      if (instrumentCategory) {
        setAccessMode(instrumentCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Instrument category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch instrument for edit mode
  const getInstrument = useCallback(async (instrumentID) => {
    try {
      setIsLoading(true);
      const instrument = await dispatch(getInstrumentByID(instrumentID));
      setInstrumentToBeEdit(instrument);
    } catch (error) {
      console.error('Error fetching instrument:', error);
      navigate('/instruments');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  // Handle create instrument
  const handleCreateInstrument = useCallback((instrumentData) => {
    return dispatch(createInstrument(instrumentData));
  }, [dispatch]);

  // Handle edit instrument
  const handleEditInstrument = useCallback((instrumentData) => {
    return dispatch(editInstrument(instrumentData));
  }, [dispatch]);

  // Handle get AWS key
  const handleGetAwsKey = useCallback((data) => {
    return dispatch(getAwsKey(data));
  }, [dispatch]);

  // Initialize component on mount
  useEffect(() => {
    // Set mode based on params
    const currentMode = params?.mode === 'edit' ? 'EDIT' : 'CREATE';
    setMode(currentMode);

    // Set access permissions
    setAccess();

    // Fetch instrument if in edit mode
    if (params?.id && currentMode === 'EDIT') {
      getInstrument(params.id);
    } else {
      setIsLoading(false);
    }
  }, [params?.id, params?.mode, getInstrument, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateInstrument
        accessMode={accessMode}
        onCreate={handleCreateInstrument}
        onEdit={handleEditInstrument}
        initialValues={instrumentToBeEdit}
        mode={mode}
        isCreating={isCreating}
        getAwsKey={handleGetAwsKey}
      />
    </section>
  );
};

export default CreateInstrumentContainer;
